<template>
  <div
    v-if="showHint"
    data-testid="remark-mention-hint"
    class="
      sf-bg-primary-subtle sf-c-info
      tw-p-2 tw-gap-10 tw-text-xs tw-leading-4 tw-w-full tw-px-2
    "
  >
    <span
      v-if="showNumberOfUsers"
      class="sf-c-primary-darker"
      data-testid="remark-mention-hint--number-of-users"
      >{{ numberOfUsers }}</span
    >
    <img
      v-if="showNumberOfUsers"
      v-tooltip.html="tooltip"
      :src="$imagesRequire('icon-followers.svg')"
      data-testid="remark-mention-hint--number-of-users-icon"
      class="tw-pb-0.5"
    />
    <span v-dompurify-html="hint" class="sf-c-primary-darker tw-ml-0.5"></span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import type { Follower } from '@app/application/workflows/types'
import type { MentionType } from '@app/shared/vue/components/new-remark/types'
import { getCompanyName } from '@app/adapters/silverfin'

interface Props {
  companyId?: number
  text?: string
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
})

const emit =
  defineEmits<{
    (e: 'hintVisibilityChanged', value: boolean): void
  }>()

const showHint = ref(false)
const I18n = useI18n()

const users = ref<{
  followers: Follower[]
  externalUsers: Follower[]
}>({
  followers: [],
  externalUsers: [],
})

const url = window.Silverfin.Routes.company_followers_and_pulse_users_path(
  window.Silverfin.currentFirmId,
  props.companyId?.toString() || window.Silverfin.currentCompanyId
)

const fetchFollowers = async () => {
  try {
    const response = await SFetch.json<typeof users.value>(url)
    if (response) {
      users.value = response
    }
  } catch (error) {
    SfLog.warn('Failed to fetch followers', { error })
  }
}

fetchFollowers()

const lastMention = computed<MentionType>(() => {
  if (!props.text) return 'none'
  if (props.text.includes('@everyone')) return 'everyone'
  if (props.text.includes('@external')) return 'external'
  if (props.text.includes('@internal')) return 'internal'
  return 'none'
})

const allUsers = computed<Follower[]>(() => {
  const byId = new Map<string, Follower>()
  const combined = [...users.value.followers, ...users.value.externalUsers]
  combined.forEach((user) => {
    byId.set(String(user.id), user)
  })
  return Array.from(byId.values())
})

const hint = computed<string | false>(() => {
  const hintMap: Record<MentionType, string | false> = {
    external: I18n.t('communication.remark_form.mention_external_html'),
    internal: I18n.t('communication.remark_form.mention_internal_html'),
    everyone: I18n.t('communication.remark_form.mention_everyone_html'),
    none: false,
  }
  return hintMap[lastMention.value]
})

const tooltip = computed<string | false>(() => {
  const companyName = getCompanyName()
  const { externalUsers, followers } = users.value

  if (lastMention.value === 'external' && externalUsers.length > 0) {
    return I18n.t('communication.remark_form.external_users_of_html', {
      name: companyName,
      external_users: externalUsers
        .map((u) => u.name)
        .sort()
        .join(', '),
    })
  }

  if (lastMention.value === 'internal' && followers.length > 0) {
    return I18n.t('communication.remark_form.followers_of_html', {
      name: companyName,
      followers: followers
        .map((u) => u.name)
        .sort()
        .join(', '),
    })
  }

  if (lastMention.value === 'everyone' && allUsers.value.length > 0) {
    return I18n.t('communication.remark_form.users_of_html', {
      name: companyName,
      users: allUsers.value
        .map((u) => u.name)
        .sort()
        .join(', '),
    })
  }

  return false
})

const numberOfUsers = computed<number>(() => {
  if (lastMention.value === 'external') return users.value.externalUsers.length
  if (lastMention.value === 'internal') return users.value.followers.length
  if (lastMention.value === 'everyone') return allUsers.value.length
  return 0
})

const showNumberOfUsers = computed<boolean>(() => {
  return numberOfUsers.value > 0
})

watch(
  lastMention,
  (newValue) => {
    showHint.value = newValue !== 'none'
    emit('hintVisibilityChanged', showHint.value)
  },
  { immediate: true }
)
</script>
